import React, { useState, useEffect } from 'react';
import { POI, Card, UpdatePOIRequest } from '../services/poiService';
import CardGallery from './CardGallery';
import CardOrderManager from './CardOrderManager';
import UserBioOverlay from './UserBioOverlay';
import POIEditForm from './POIEditForm';
import { getReadableCategoryName } from '../utils/categoryUtils';
import { useAuth } from '../contexts/AuthContext';
import { Trash2, ListPlus, ArrowUpDown, Edit } from 'lucide-react';
import { listService, List } from '../services/listService';
import poiService from '../services/poiService';

interface POIPopupProps {
  poi: POI;
  cards: Card[];
  isLoading: boolean;
  onClose: () => void;
  onCardDelete?: (cardIdV2: string) => void;
  onCardEdit?: (card: Card) => void;
  onPOIDelete?: (poiIdV2: string) => void;
  onPOIEdit?: (updatedPOI: POI) => void;
}

const POIPopup: React.FC<POIPopupProps> = ({ poi, cards, isLoading, onClose, onCardDelete, onCardEdit, onPOIDelete, onPOIEdit }) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [showOrderManager, setShowOrderManager] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [cardOrder, setCardOrder] = useState<string[]>([]);
  const { user } = useAuth();

  // Check if current user is a creator of any cards for this POI
  const isCardCreator = React.useMemo(() => {
    if (!user || cards.length === 0) return false;
    return cards.some(card => card.user_id === user.id);
  }, [user, cards]);

  // Load saved card order from backend on mount
  useEffect(() => {
    const loadCardOrder = async () => {
      if (!user || !poi.id) return;
      
      try {
        const response = await poiService.fetchCardOrder(poi.id);
        if (response.success && response.data.ordered_card_ids.length > 0) {
          setCardOrder(response.data.ordered_card_ids);
        }
      } catch (error) {
        console.error('Failed to load card order:', error);
        // Fallback to localStorage if backend fails
        const savedOrder = localStorage.getItem(`cardOrder_${poi.id}`);
        if (savedOrder) {
          try {
            const parsedOrder = JSON.parse(savedOrder);
            setCardOrder(parsedOrder);
          } catch (e) {
            console.error('Failed to parse saved card order from localStorage:', e);
          }
        }
      }
    };

    loadCardOrder();
  }, [poi.id, user]);

  // Prevent scrolling on the body when popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
  };

  const handleCloseUserOverlay = () => {
    setSelectedUserId(null);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (onPOIDelete) {
      onPOIDelete(poi.id);
    }
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleSaveCardOrder = async (orderedCardIds: string[]) => {
    setCardOrder(orderedCardIds);
    
    try {
      // Save to backend
      await poiService.saveCardOrder(poi.id, orderedCardIds);
      
      // Also save to localStorage as backup
      localStorage.setItem(`cardOrder_${poi.id}`, JSON.stringify(orderedCardIds));
      
      setShowOrderManager(false);
    } catch (error) {
      console.error('Failed to save card order to backend:', error);
      alert('Failed to save card order. Please try again.');
    }
  };

  const handleCancelOrderManager = () => {
    setShowOrderManager(false);
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
  };

  const handlePOIUpdate = async (updateData: UpdatePOIRequest) => {
    try {
      setIsUpdating(true);
      const response = await poiService.updatePOI(poi.id, updateData);
      
      if (response.success && onPOIEdit) {
        onPOIEdit(response.data);
      }
      
      setShowEditModal(false);
    } catch (error: any) {
      console.error('Failed to update POI:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update POI. Please try again.';
      alert(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-2 sm:p-4 overflow-hidden" onClick={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col mobile-viewport-fix" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-secondary-50 flex-shrink-0">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">{poi.name}</h2>
              {poi.category && (
                <div className="mt-1">
                  <span className="inline-block bg-primary-100 text-primary-800 text-xs sm:text-sm px-2 py-1 rounded-full font-medium">
                    {getReadableCategoryName(poi.category)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
              {isCardCreator && cards.length > 1 && (
                <button
                  onClick={() => setShowOrderManager(true)}
                  className="text-purple-500 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors p-2 rounded-full hover:bg-purple-50 dark:hover:bg-purple-900/20 touch-target"
                  title="Reorder Cards"
                >
                  <ArrowUpDown className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}
              {user && (
                <button
                  onClick={() => setShowAddToListModal(true)}
                  className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-2 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 touch-target"
                  title="Add to List"
                >
                  <ListPlus className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={handleEditClick}
                  className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors p-2 rounded-full hover:bg-green-50 dark:hover:bg-green-900/20 touch-target"
                  title="Edit POI"
                >
                  <Edit className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}
              {isAdmin && onPOIDelete && (
                <button
                  onClick={handleDeleteClick}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 touch-target"
                  title="Delete POI"
                >
                  <Trash2 className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 touch-target"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content - improved height calculation for mobile */}
          <div className="p-4 sm:p-6 flex-1 min-h-0 flex flex-col mobile-section" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {isLoading ? (
              <div className="flex items-center justify-center py-8 sm:py-12">
                <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-primary-500 dark:border-primary-400"></div>
                <span className="ml-3 sm:ml-4 text-gray-600 dark:text-gray-400 dark:text-gray-500 text-sm sm:text-lg">Loading information cards...</span>
              </div>
            ) : (
              <CardGallery 
                cards={cards} 
                poi={poi}
                onCardDelete={onCardDelete}
                onCardEdit={onCardEdit}
                onUserClick={handleUserClick}
                customOrder={cardOrder.length > 0 ? cardOrder : undefined}
              />
            )}
          </div>


        </div>
      </div>
      
      {/* User Bio Overlay - appears on top of POI popup */}
      {selectedUserId && (
        <UserBioOverlay 
          userId={selectedUserId}
          onClose={handleCloseUserOverlay}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCancelDelete}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete POI</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <span className="font-semibold">{poi.name}</span>? 
              This will also delete all associated cards ({cards.length} card{cards.length !== 1 ? 's' : ''}).
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-white bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete POI
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add to List Modal */}
      {showAddToListModal && user && (
        <AddToListModal
          poi={poi}
          onClose={() => setShowAddToListModal(false)}
        />
      )}
      
      {/* Card Order Manager */}
      {showOrderManager && (
        <CardOrderManager
          cards={cards}
          onSave={handleSaveCardOrder}
          onCancel={handleCancelOrderManager}
        />
      )}
      
      {/* POI Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCancelEdit}>
          <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <POIEditForm
              poi={poi}
              onSubmit={handlePOIUpdate}
              onCancel={handleCancelEdit}
              isLoading={isUpdating}
            />
          </div>
        </div>
      )}
    </>
  );
};

// Add to List Modal Component
interface AddToListModalProps {
  poi: POI;
  onClose: () => void;
}

const AddToListModal: React.FC<AddToListModalProps> = ({ poi, onClose }) => {
  const [lists, setLists] = useState<List[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingToListId, setAddingToListId] = useState<string | null>(null);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      setIsLoading(true);
      const data = await listService.getAllLists();
      setLists(data.lists);
      setError(null);
    } catch (err) {
      setError('Failed to load lists');
      console.error('Error fetching lists:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToList = async (listUuid: string) => {
    try {
      setAddingToListId(listUuid);
      await listService.addPOIsToList(listUuid, { poi_uuids: [poi.id] });
      alert('Added to list successfully!');
      onClose();
    } catch (err: any) {
      // Check for common error responses
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
      
      if (errorMessage.toLowerCase().includes('already in the list')) {
        alert('This place is already in that list');
      } else {
        alert('Failed to add to list');
      }
      console.error('Error adding to list:', err);
    } finally {
      setAddingToListId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                  Add "{poi.name}" to a list
                </h3>

                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading lists...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                  </div>
                ) : lists.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      You don't have any lists yet.
                    </p>
                    <a
                      href="/lists"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Create your first list
                    </a>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {lists.map((list) => (
                      <button
                        key={list.uuid}
                        onClick={() => handleAddToList(list.uuid)}
                        disabled={addingToListId === list.uuid}
                        className="w-full text-left p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white">{list.name}</p>
                            {list.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                                {list.description}
                              </p>
                            )}
                          </div>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            {list.poi_count} {list.poi_count === 1 ? 'place' : 'places'}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POIPopup;
